import React, { useEffect } from 'react';
import { HookhouseFormData, QUICK_BLEND_GROUPS } from '@/types';
import { Settings2, RotateCcw, Activity, Library, BookOpen, X, Users } from 'lucide-react';
import { Tooltip } from './Tooltip';
import { DEFAULT_FORM_VALUES, MOOD_GROUPS } from '@/constants';
import { useStylePresets } from '@hooks/useStylePresets';
import { useVocalConfig } from '@context/VocalConfigContext';
import { useStudioForm } from '@context/StudioFormContext';
import { VocalPackSection } from './VocalForge/VocalPackSection';
import { HookHouseTemplate, TemplateConfig } from '@/types/template.types';
import { Button } from './ui/Button';
import { Input, TextArea, Select } from './ui/Form';
import { DescriptiveSelect } from './ui/DescriptiveSelect';
import { RhymeStructurePicker } from './RhymeStructurePicker';
import { Badge } from './ui/Badge';
import { usePromptPreview } from '@hooks/usePromptPreview';
import { StudioPromptPreview } from './StudioPromptPreview';
import { PolishInput } from './LyricPolisher/PolishInput';
import type { PersonaOverride } from '@/types/persona.types';

interface SunoConfigTabProps {
  onSubmit: (data: HookhouseFormData, vocalArchitecture?: string) => void;
  isGenerating: boolean;
  onMoodChange: (mood: string[]) => void;
  onBlendChange?: (blend: string[]) => void;
  onOpenBlender?: () => void;
  onOpenLibrary: () => void;
  activeMoods: string[];
  activeBlends: string[];
  onLoadTemplate: (template: HookHouseTemplate) => void;
  getCurrentConfig: () => TemplateConfig;
  personaOverride?: PersonaOverride | null;
  onParliamentWrite?: (data: HookhouseFormData, vocalArchitecture?: string) => void;
  isParliamentWriting?: boolean;
  onParliamentPolish?: (lyrics: string, data: HookhouseFormData, vocalArchitecture?: string) => void;
  isParliamentPolishing?: boolean;
}

export const SunoConfigTab: React.FC<SunoConfigTabProps> = ({
  onSubmit,
  isGenerating,
  onMoodChange,
  onBlendChange,
  onOpenBlender,
  onOpenLibrary,
  activeMoods,
  activeBlends,
  personaOverride,
  onParliamentWrite,
  isParliamentWriting,
  onParliamentPolish,
  isParliamentPolishing,
}) => {
  const {
    formData,
    setFormData,
    updateField,
    resetForm,
    narrativeBlueprint,
    setNarrativeBlueprint,
  } = useStudioForm();

  const { getActiveVocalArchitecture, config: vocalConfig } = useVocalConfig();
  const { allPresets: STYLE_PRESETS } = useStylePresets();

  const previewData = usePromptPreview(formData, personaOverride);

  useEffect(() => {
    const needsUpdate =
      JSON.stringify(formData.mood) !== JSON.stringify(activeMoods) ||
      JSON.stringify(formData.blends) !== JSON.stringify(activeBlends) ||
      formData.vocalPackKey !== (vocalConfig.mode === 'single' ? (vocalConfig.singlePackKey || 'None') : 'None');

    if (needsUpdate) {
      setFormData(prev => ({
        ...prev,
        mood: activeMoods,
        blends: activeBlends,
        vocalPackKey: vocalConfig.mode === 'single' ? (vocalConfig.singlePackKey || 'None') : 'None'
      }));
    }
  }, [activeMoods, activeBlends, vocalConfig, formData.mood, formData.blends, formData.vocalPackKey, setFormData]);

  const isBypassActive = formData.stylePreset !== 'None';
  const isStylePresetActive = formData.stylePreset !== 'None';

  const handleBlendToggle = (blend: string) => {
    setFormData(prev => {
      let newBlends;
      if (prev.blends.includes(blend)) {
        newBlends = prev.blends.filter(b => b !== blend);
      } else {
        if (prev.blends.length >= 3) return prev;
        newBlends = [...prev.blends, blend];
      }
      if (onBlendChange) onBlendChange(newBlends);
      return { ...prev, blends: newBlends };
    });
  };

  const handleMoodToggle = (mood: string) => {
    setFormData(prev => {
      let newMoods;
      if (prev.mood.includes(mood)) {
        newMoods = prev.mood.filter(m => m !== mood);
      } else {
        if (prev.mood.length >= 4) return prev;
        newMoods = [...prev.mood, mood];
      }
      onMoodChange(newMoods);
      return { ...prev, mood: newMoods };
    });
  };

  const handleClear = () => {
    if (window.confirm("Are you sure you want to clear all fields?")) {
      resetForm();
      onMoodChange(DEFAULT_FORM_VALUES.mood);
      if (onBlendChange) onBlendChange(DEFAULT_FORM_VALUES.blends);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const vocalArch = getActiveVocalArchitecture();
    onSubmit(formData, vocalArch);
  };

  // Build style preset options for Select component
  const stylePresetOptions = [
    { value: 'None', label: 'None (Use Manual Configuration)' },
    ...Object.keys(STYLE_PRESETS).map(presetName => ({
      value: presetName,
      label: presetName.replace('[Redux]', '[R]')
    }))
  ];

  return (
    <div className="space-y-8">
      {/* Narrative Engine Active Banner */}
      {narrativeBlueprint && (
        <div className="flex items-center gap-3 bg-amber-950/20 border border-amber-900/30 rounded-xl px-4 py-3">
          <BookOpen size={16} className="text-accent shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-black text-amber-400 uppercase tracking-widest">
              Narrative Engine Active
            </p>
            <p className="text-[11px] text-slate-400 mt-0.5 truncate">
              {narrativeBlueprint.arc.label} / {narrativeBlueprint.tone.label} / {narrativeBlueprint.character.stance}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setNarrativeBlueprint(null)}
            className="text-slate-500 hover:text-red-400 transition-colors shrink-0"
            title="Remove narrative"
          >
            <X size={14} />
          </button>
        </div>
      )}

      <div className="space-y-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Session Configuration Section */}
          <div className="border-l-4 border-l-accent rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-xs font-black text-slate-400 uppercase tracking-[0.2em] text-left">Session Configuration</h2>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="xs"
                  onClick={onOpenLibrary}
                  leftIcon={<Library size={12} />}
                  className="text-accent hover:text-amber-400 border border-slate-800 hover:border-slate-700 bg-slate-900/30"
                >
                  Vault
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="xs"
                  onClick={handleClear}
                  leftIcon={<RotateCcw size={12} />}
                  className="text-slate-400 hover:text-slate-300 border border-slate-800 hover:border-slate-700 bg-slate-900/30"
                >
                  Clear
                </Button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-400 mb-1 text-left">
                <Tooltip text="The core concept, title, or lyric fragment to build the song around.">
                  <span className="cursor-help border-b border-dashed border-slate-700 hover:border-slate-500 transition-colors">Song Title or Seed Idea</span>
                </Tooltip>
                <span className="text-accent ml-1">*</span>
              </label>
              <Input
                required
                placeholder="e.g. Neon ghosts on the interstate..."
                value={formData.seed}
                onChange={(e) => updateField('seed', e.target.value)}
                className="bg-slate-950 rounded-xl"
              />
            </div>

            <div className="mt-4">
              <RhymeStructurePicker
                rhyme={formData.rhymeScheme}
                structure={formData.songStructure}
                onRhymeChange={(rhyme) => updateField('rhymeScheme', rhyme)}
                onStructureChange={(structure) => updateField('songStructure', structure)}
              />
            </div>
          </div>

          {/* Style Preset + Vocal Configuration + Generation Intelligence */}
          <div className="border-l-4 border-l-accent rounded-xl p-4 space-y-4">
            <div className={isStylePresetActive ? 'border border-accent rounded-xl p-3 -m-3 shadow-[0_0_10px_rgba(245,158,11,0.2)]' : ''}>
              <label className="block text-sm font-medium text-slate-400 mb-1 text-left">
                <Tooltip text="Select a total blueprint override.">
                   <span className="cursor-help border-b border-dashed border-slate-700 hover:border-slate-500 transition-colors">Style Preset (Override)</span>
                </Tooltip>
              </label>
              <Select
                value={formData.stylePreset}
                onChange={(e) => updateField('stylePreset', e.target.value)}
                options={stylePresetOptions}
                className="bg-slate-950 rounded-xl text-accent font-semibold"
              />
              {formData.stylePreset !== 'None' && (
                <p className="mt-2 text-[11px] text-slate-400 leading-tight text-left">
                  <span className="text-accent font-bold">ACTIVE:</span> {STYLE_PRESETS[formData.stylePreset]}
                </p>
              )}
            </div>

            {/* Instrumental Toggle */}
            <div className="flex items-center justify-between p-3 rounded-xl border border-slate-800 bg-slate-900/30 mb-3">
              <div>
                <span className="text-sm font-bold text-slate-200">Instrumental</span>
                <p className="text-[11px] text-slate-500 mt-0.5">No vocals — skips VocalForge architecture entirely</p>
              </div>
              <button
                type="button"
                onClick={() => updateField('isInstrumental', !formData.isInstrumental)}
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  formData.isInstrumental ? 'bg-accent' : 'bg-slate-700'
                }`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                    formData.isInstrumental ? 'translate-x-6' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            <div className={isBypassActive || formData.isInstrumental ? 'opacity-40 grayscale pointer-events-none' : ''}>
              <VocalPackSection onOpenBlender={onOpenBlender} />
            </div>

          {/* Musical Blend Section */}
          <div className={`border-l-4 border-l-accent rounded-xl p-4 ${isBypassActive ? 'opacity-40 grayscale pointer-events-none' : ''}`}>
            <label className={`block text-sm font-medium mb-2 transition-colors text-left ${isBypassActive ? 'text-slate-400' : 'text-slate-400'}`}>
              <Tooltip text="Combine up to 3 genres."><span className="cursor-help border-b border-dashed border-slate-700">Musical Blend</span></Tooltip>
              <span className="text-xs text-slate-400 ml-2">(Select up to 3)</span>
            </label>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-4">
              {QUICK_BLEND_GROUPS.map(group => (
                <div key={group.name} className="space-y-3 bg-slate-950/40 p-4 rounded-2xl border-l-4 border-l-accent">
                  <h4 className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] border-b border-slate-800 pb-2 mb-2">{group.name}</h4>
                  <div className="flex flex-wrap gap-2">
                    {group.options.map(option => (
                      <Tooltip key={option.label} text={option.description}>
                        <Badge
                          variant={formData.blends.includes(option.label) && !isBypassActive ? 'primary' : 'default'}
                          size="sm"
                          onClick={() => handleBlendToggle(option.label)}
                          className={`cursor-pointer transition-all ${
                            formData.blends.includes(option.label) && !isBypassActive
                              ? 'shadow-lg scale-105'
                              : 'hover:border-slate-600 hover:text-slate-200'
                          } ${isBypassActive ? 'pointer-events-none' : ''}`}
                        >
                          {option.label}
                        </Badge>
                      </Tooltip>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Mood Architecture Section */}
          <div className="border-l-4 border-l-accent rounded-xl p-4">
            <label className="block text-sm font-medium text-slate-400 mb-2 text-left">
               <Tooltip text="Select mood tags."><span className="cursor-help border-b border-dashed border-slate-700">Mood Architecture</span></Tooltip>
               <span className="text-xs text-slate-400 ml-2">(Select up to 4)</span>
            </label>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-4 mb-4">
              {MOOD_GROUPS.map(group => (
                <div key={group.name} className="space-y-3 bg-slate-950/40 p-4 rounded-2xl border-l-4 border-l-accent">
                  <h4 className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] border-b border-slate-800 pb-2 mb-2">{group.name}</h4>
                  <div className="flex flex-wrap gap-2">
                    {group.options.map(option => (
                      <Tooltip key={option.label} text={option.description}>
                        <Badge
                          variant={formData.mood.includes(option.label) ? 'primary' : 'default'}
                          size="sm"
                          onClick={() => handleMoodToggle(option.label)}
                          className={`cursor-pointer transition-all ${
                            formData.mood.includes(option.label)
                              ? 'shadow-lg scale-105'
                              : 'hover:border-slate-600 hover:text-slate-200'
                          }`}
                        >
                          {option.label}
                        </Badge>
                      </Tooltip>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center gap-4 flex-wrap">
              <label className="flex items-center gap-2 cursor-pointer group w-max">
                  <input type="checkbox" checked={formData.explicit} onChange={(e) => updateField('explicit', e.target.checked)} className="sr-only peer" />
                  <div className="w-10 h-6 bg-slate-800 border border-slate-700 rounded-full peer-checked:bg-amber-600 transition-all relative after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-slate-400 after:rounded-full after:h-5 after:w-5 peer-checked:after:translate-x-4"></div>
                  <span className={`text-sm font-medium transition-colors ${formData.explicit ? 'text-amber-400' : 'text-slate-400'}`}>Explicit Lyrics</span>
              </label>

              <div className="flex items-center gap-2" title='Studio forbids "live" language in style tags entirely (Suno reads any mention of "live" as a request for concert/crowd atmosphere, even innocuous phrasing like "live room feel"). Live explicitly wants that concert character.'>
                <span className="text-[11px] font-black uppercase tracking-widest text-slate-500">Style</span>
                <div className="flex bg-slate-800 border border-slate-700 rounded-full p-0.5">
                  {(['studio', 'live'] as const).map(mode => (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => updateField('styleMode', mode)}
                      className={`px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-widest transition-all ${
                        (formData.styleMode ?? 'studio') === mode
                          ? 'bg-accent text-slate-950'
                          : 'text-slate-400 hover:text-slate-200'
                      }`}
                    >
                      {mode}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-slate-950/40 p-5 rounded-2xl border-l-4 border-l-accent space-y-6">
             <h3 className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] flex items-center gap-2">
               <Activity size={14} className="text-accent" /> Generation Intelligence
             </h3>

             <div className="space-y-4">
               <div className="space-y-2">
                 <div className="flex justify-between items-center text-[11px] font-black uppercase tracking-widest">
                   <span className="text-slate-400">Style Strength</span>
                   <span className="text-accent font-mono">{formData.styleStrength}%</span>
                 </div>
                 <input
                   type="range"
                   min="0"
                   max="100"
                   value={formData.styleStrength}
                   onChange={(e) => updateField('styleStrength', parseInt(e.target.value))}
                   className="w-full h-1.5 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-accent"
                 />
                 <p className="text-[11px] text-slate-400 font-bold uppercase tracking-tighter text-left">Adherence to style tags vs model creativity</p>
               </div>

               <div className="space-y-2">
                 <div className="flex justify-between items-center text-[11px] font-black uppercase tracking-widest">
                   <span className="text-slate-400">Weirdness</span>
                   <span className="text-red-400 font-mono">{formData.weirdness}%</span>
                 </div>
                 <input
                   type="range"
                   min="0"
                   max="100"
                   value={formData.weirdness}
                   onChange={(e) => updateField('weirdness', parseInt(e.target.value))}
                   className="w-full h-1.5 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-red-500"
                 />
                 <p className="text-[11px] text-slate-400 font-bold uppercase tracking-tighter text-left">Level of sonic experimentalism & artifacts</p>
               </div>
             </div>
          </div>
          </div>

          {/* POV & Setting Section */}
          <div className="border-l-4 border-l-accent rounded-xl p-4">
            <label className="block text-sm font-medium text-slate-400 mb-1 text-left">POV & Setting</label>
            <TextArea
              rows={2}
              placeholder="Truck cab, 3AM, raining."
              value={formData.povSetting}
              onChange={(e) => updateField('povSetting', e.target.value)}
              className="bg-slate-950 rounded-xl"
            />
          </div>

          {/* Must Haves & Avoid Section */}
          <div className="border-l-4 border-l-accent rounded-xl p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1 text-left">Must Haves (Themes)</label>
                <TextArea
                  rows={3}
                  placeholder="Loss, whiskey."
                  value={formData.themesToHit}
                  onChange={(e) => updateField('themesToHit', e.target.value)}
                  className="bg-slate-950 rounded-xl border-l-4 border-l-green-900"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1 text-left">Avoid (Anti-Themes)</label>
                <TextArea
                  rows={3}
                  placeholder="Neon, cyber."
                  value={formData.themesToAvoid}
                  onChange={(e) => updateField('themesToAvoid', e.target.value)}
                  className="bg-slate-950 rounded-xl border-l-4 border-l-red-900 focus:border-red-500"
                />
              </div>
            </div>
          </div>

          <div className="border-l-4 border-l-accent rounded-xl p-4">
            <div className="flex items-center gap-2 mb-4 text-slate-400 text-xs uppercase tracking-widest font-bold text-left">
              <Settings2 size={14} />
              <span>Technical Constraints</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="text-left">
                <label className="block text-xs text-slate-400 mb-1">Instruments</label>
                <Input
                  placeholder="Hammond B3"
                  value={formData.instruments}
                  onChange={(e) => updateField('instruments', e.target.value)}
                  size="sm"
                  className="bg-slate-950 rounded-xl"
                />
              </div>
              <div className="text-left">
                <label className="block text-xs text-slate-400 mb-1">Structure/BPM</label>
                <Input
                  placeholder="120BPM, Key of E"
                  value={formData.constraints}
                  onChange={(e) => updateField('constraints', e.target.value)}
                  size="sm"
                  className="bg-slate-950 rounded-xl"
                />
              </div>
            </div>
          </div>

          {/* Prompt Preview */}
          <StudioPromptPreview data={previewData} />

          <div className="pt-2 sticky bottom-4 z-10 space-y-2">
            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              disabled={!formData.seed}
              isLoading={isGenerating}
              loadingText="Architecting..."
              leftIcon={!isGenerating ? <Activity className="w-5 h-5" /> : undefined}
              className="py-4 rounded-xl text-lg shadow-lg shadow-amber-500/10"
            >
              Generate Suno Prompt
            </Button>
            <Button
              type="button"
              variant="outline"
              size="lg"
              fullWidth
              disabled={!formData.seed || isGenerating || isParliamentWriting}
              isLoading={isParliamentWriting}
              loadingText="Parliament Writing..."
              leftIcon={<Users size={20} />}
              onClick={() => onParliamentWrite?.(formData, getActiveVocalArchitecture())}
              className="py-3 rounded-xl text-sm border-amber-500/50 text-amber-400 hover:bg-amber-900/20"
            >
              Parliament Write
            </Button>
            <PolishInput
              onPolish={(lyrics) => onParliamentPolish?.(lyrics, formData, getActiveVocalArchitecture())}
              isPolishing={isParliamentPolishing || false}
              disabled={isGenerating || isParliamentWriting || false}
              activeBlends={activeBlends}
              onBlendChange={onBlendChange}
            />
          </div>
        </form>
      </div>
    </div>
  );
};
